/** Generic file upload/replace/clear widget for a single attachment field.
 *  Modeled on `ImageUploadField`, but for any file: instead of an image
 *  preview it shows the current attachment's name as a download link. The
 *  controlled tri-state matches ImageUploadField exactly:
 *   - `null`      → keep the current file (no change)
 *   - `File`      → replace with the staged file (sent as a multipart part)
 *   - `'cleared'` → remove the current file (sent as an explicit null)
 */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, Chip, FormLabel, Link as MuiLink, Stack, Typography } from '@mui/material';

export type AttachmentFieldValue = File | 'cleared' | null;

export function AttachmentUploadField({
  label, currentUrl, currentName, value, onChange, canManage, accept,
}: {
  label: string;
  currentUrl: string | null;
  currentName?: string | null;
  value: AttachmentFieldValue;
  onChange: (v: AttachmentFieldValue) => void;
  canManage: boolean;
  accept?: string;
}) {
  const hasCurrent = Boolean(currentUrl || currentName);

  return (
    <Box>
      <FormLabel sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5, display: 'block' }}>{label}</FormLabel>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
        <Box sx={{ minWidth: 0 }}>
          {value instanceof File ? (
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value.name}</Typography>
          ) : value === 'cleared' ? (
            <Typography variant="body2" color="text.disabled">Will be removed</Typography>
          ) : hasCurrent ? (
            currentUrl ? (
              <MuiLink href={currentUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-word' }}>
                {currentName || 'Download'}
              </MuiLink>
            ) : (
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{currentName}</Typography>
            )
          ) : (
            <Typography variant="body2" color="text.disabled">No file</Typography>
          )}
        </Box>

        <Stack spacing={0.5}>
          <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={!canManage}>
            {value instanceof File || hasCurrent ? 'Replace' : 'Upload'}
            <input hidden type="file" {...(accept ? { accept } : {})}
              onChange={(e) => { const f = e.target.files?.[0] ?? null; if (f) onChange(f); e.target.value = ''; }} />
          </Button>
          {value instanceof File && (
            <Chip size="small" color="info" label={`Staged: ${value.name}`}
              onDelete={canManage ? () => onChange(null) : undefined} />
          )}
          {value === 'cleared' && (
            <Button size="small" color="inherit" startIcon={<UndoIcon />} onClick={() => onChange(null)}>
              Undo remove
            </Button>
          )}
          {value === null && hasCurrent && (
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={!canManage}
              onClick={() => onChange('cleared')}>
              Remove
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
